/**
 * Membership Gift Styling
 * Disables quantity selection and hides price for membership-linked gifts
 */

class MembershipGiftStyling {
  constructor() {
    this.LINKED_PROPERTY_KEY = '_membership_gift';
    this.LINKED_PROPERTY_VALUE = 'true';
    this.latestCart = null;
    this.applyTimer = null;
    this.fetchPromise = null;
    this.init();
  }

  init() {
    // Add CSS rules immediately
    this.injectStyles();
    
    // Apply styling on load (faster)
    this.scheduleApply({ delay: 100 });
    
    // Listen for cart updates
    document.addEventListener('cart:updated', (event) => {
      this.scheduleApply({ delay: 50, cartData: event.detail?.cartData });
    });
    
    // Listen for cart-drawer:updated for better timing
    document.addEventListener('cart-drawer:updated', (event) => {
      this.scheduleApply({ delay: 50, cartData: event.detail?.cartData });
    });
    
    // Watch for DOM changes in cart drawer
    this.observeCartDrawer();
  }

  hasFullCartSnapshot(cart) {
    return Boolean(cart && Array.isArray(cart.items) && typeof cart.item_count !== 'undefined');
  }

  scheduleApply({ delay = 0, cartData = null } = {}) {
    if (this.hasFullCartSnapshot(cartData)) {
      this.latestCart = cartData;
      window.cartState?.setCart(cartData);
    }

    clearTimeout(this.applyTimer);
    this.applyTimer = setTimeout(() => {
      this.applyGiftStyling(this.latestCart);
    }, delay);
  }

  async applyGiftStyling(cartOverride) {
    try {
      const cart = this.hasFullCartSnapshot(cartOverride) ? cartOverride : await this.fetchCart();
      this.latestCart = cart;
      
      // Find all membership gift items
      const giftItems = cart.items.filter(item => 
        item.properties?.[this.LINKED_PROPERTY_KEY] === this.LINKED_PROPERTY_VALUE
      );
      
      if (giftItems.length === 0) {
        return;
      }
      
      // Apply styling to each gift item in the DOM
      giftItems.forEach(giftItem => {
        this.styleGiftItem(giftItem);
      });
      
    } catch (error) {
      console.error('[Gift Styling] Failed to apply styling:', error);
    }
  }

  styleGiftItem(giftItem) {
    // Find the cart item in the DOM by matching variant ID or key
    const cartItems = document.querySelectorAll('.cart-item, [data-cart-item], .cart__item');
    
    cartItems.forEach(cartItemEl => {
      // Try different ways to match the item
      const variantId = cartItemEl.getAttribute('data-variant-id') || 
                        cartItemEl.querySelector('[data-variant-id]')?.getAttribute('data-variant-id') ||
                        cartItemEl.querySelector('[name*="updates"]')?.getAttribute('data-index');
      
      const itemKey = cartItemEl.getAttribute('data-key') ||
                      cartItemEl.querySelector('[data-cart-item-key]')?.getAttribute('data-cart-item-key');
      
      // Check if this cart item matches our gift item
      const matches = (variantId && variantId === giftItem.variant_id.toString()) ||
                      (itemKey && itemKey === giftItem.key);
      
      if (!matches) return;
      
      // Add a class to the cart item for easier targeting
      cartItemEl.classList.add('membership-gift-item');
      
      // 1. Disable quantity selector
      this.disableQuantitySelector(cartItemEl);
      
      // 2. Hide price
      this.hidePrice(cartItemEl);
      
      // 3. Add "Gift" badge (optional visual indicator)
      this.addGiftBadge(cartItemEl);
    });
  }

  disableQuantitySelector(cartItemEl) {
    // Find quantity input/selector
    const quantityInput = cartItemEl.querySelector('input[type="number"]') ||
                          cartItemEl.querySelector('.quantity__input') ||
                          cartItemEl.querySelector('[name*="updates"]');
    
    if (quantityInput) {
      quantityInput.disabled = true;
      quantityInput.style.opacity = '0.5';
      quantityInput.style.cursor = 'not-allowed';
    }
    
    // Disable quantity buttons (plus/minus)
    const quantityButtons = cartItemEl.querySelectorAll('.quantity__button, button[name="plus"], button[name="minus"]');
    quantityButtons.forEach(btn => {
      btn.disabled = true;
      btn.style.opacity = '0.5';
      btn.style.cursor = 'not-allowed';
      btn.style.pointerEvents = 'none';
    });
    
    // Hide the entire quantity selector if desired (alternative approach)
    const quantitySelector = cartItemEl.querySelector('.quantity, .quantity-selector, .cart-item__quantity');
    if (quantitySelector) {
      // Option 1: Hide completely
      // quantitySelector.style.display = 'none';
      
      // Option 2: Just disable (current approach above)
      quantitySelector.style.opacity = '0.5';
      quantitySelector.style.pointerEvents = 'none';
    }
  }

  hidePrice(cartItemEl) {
    // Find all price elements
    const priceSelectors = [
      '.cart-item__price',
      '.cart-item__totals',
      '.price',
      '[data-cart-item-price]',
      '.cart__item-price',
      '.product-option dd' // Sometimes prices are in definition lists
    ];
    
    priceSelectors.forEach(selector => {
      const priceEls = cartItemEl.querySelectorAll(selector);
      priceEls.forEach(priceEl => {
        // Check if this element contains price information
        const text = priceEl.textContent;
        if (text.includes('$') || text.includes('USD') || priceEl.classList.contains('price')) {
          priceEl.style.display = 'none';
        }
      });
    });
    
    // Also hide any "Regular price" or "Sale price" labels
    const priceLabels = cartItemEl.querySelectorAll('.cart-item__price-wrapper, .price-wrapper');
    priceLabels.forEach(label => {
      if (label.textContent.includes('$')) {
        label.style.display = 'none';
      }
    });
  }

  addGiftBadge(cartItemEl) {
    // Check if badge already exists
    if (cartItemEl.querySelector('.membership-gift-badge')) {
      return;
    }
    
    // Create a "Gift" badge
    const badge = document.createElement('span');
    badge.className = 'membership-gift-badge';
    badge.textContent = 'Free $20 Gift';
    badge.style.cssText = `
        display: inline-block;
        background: #00765e !important;
        color: white;
        padding: 4px 12px;
        border-radius: 8px;
        font-weight: 600;
        font-size: 11px;
        letter-spacing: 0.5px;
        margin-left: 8px;
        vertical-align: middle;
        font-family: 'Poppins';
    `;
    
    // Try to insert the badge near the product title
    const titleEl = cartItemEl.querySelector('.cart-item__name, .cart-item__title, .product-title, h3, h4');
    if (titleEl) {
      titleEl.appendChild(badge);
    }
  }

  observeCartDrawer() {
    // Watch for changes in the cart drawer
    const observer = new MutationObserver((mutations) => {
      // Debounce rapid mutations
      this.scheduleApply({ delay: 100 });
    });

    // Observe the cart drawer container
    const cartDrawer = document.querySelector('#CartDrawer, cart-drawer, .cart-drawer, #cart-drawer');
    if (cartDrawer) {
      observer.observe(cartDrawer, {
        childList: true,
        subtree: true
      });
    }
    
    // The items container normally lives inside the drawer. Observe it only as
    // a fallback so the same DOM mutation does not schedule styling twice.
    const cartItems = document.querySelector('#CartDrawer-CartItems, .cart-items, .cart__items');
    if (!cartDrawer && cartItems) {
      observer.observe(cartItems, {
        childList: true,
        subtree: true
      });
    }
  }

  async fetchCart() {
    if (window.cartState?.getCart) {
      const cart = await window.cartState.getCart();
      this.latestCart = cart;
      return cart;
    }

    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    this.fetchPromise = fetch('/cart.js')
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to fetch cart');
        }

        return response.json();
      })
      .finally(() => {
        this.fetchPromise = null;
      });

    return this.fetchPromise;
  }

  injectStyles() {
    const style = document.createElement('style');
    style.id = 'membership-gift-styling';
    style.textContent = `
      /* Membership Gift Item Styling */
      .membership-gift-item {
        position: relative;
      }
      
      /* Ensure prices stay hidden */
      .membership-gift-item .cart-item__price,
      .membership-gift-item .cart-item__totals,
      .membership-gift-item .price {
        display: none !important;
      }
    `;
    
    // Remove existing style if present
    const existing = document.getElementById('membership-gift-styling');
    if (existing) {
      existing.remove();
    }
    
    document.head.appendChild(style);
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.membershipGiftStyling = new MembershipGiftStyling();
  });
} else {
  // DOM already loaded
  window.membershipGiftStyling = new MembershipGiftStyling();
}
